import { forwardRef, useState } from 'react';

// material-ui
import {
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    IconButton,
    Grid,
    Stack,
    TextField,
    Zoom,
    DialogContent,
    InputAdornment
} from '@mui/material';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import validationSchema from 'utils/schema/PasswordSchema';
import useApi from 'hooks/useApi';

// third-party
import { useFormik } from 'formik';
import UserService from 'services/UserService';

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const ChangePassword = ({ selectedUuid, open, handleClose, setReload }) => {

    const [showPassword, setShowPassword] = useState(false)

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    }

    const submitHandler = () => {
        changeUserPasswordByUuid.execute({ password: formik.values.password }, selectedUuid);
    };

    const closeDialog = () => {
        formik.resetForm();
        handleClose();
    };

    const createSuccessHandler = () => {
        formik.setSubmitting(true);
        formik.resetForm();
        setReload(r => !r);
        handleClose();
    };

    const changeUserPasswordByUuid = useApi(UserService.changeUserPasswordByUuid, createSuccessHandler, true)

    const formik = useFormik({
        initialValues: {
            password: '',
            confirm_password: ''
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: submitHandler
    });

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDialog}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogContent sx={{ p: 0 }}>
                <MainCard
                    title={'Change Password'}
                    secondary={
                        <IconButton onClick={closeDialog} size="large" aria-label="Close Edit Board">
                            <HighlightOffTwoToneIcon fontSize="small" />
                        </IconButton>
                    }
                    sx={{ border: 0, textAlign: 'center' }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={gridSpacing} sx={{ justifyContent: 'center' }}>
                            <Grid item xs={12} sm={10}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="password"
                                        label="Password*"
                                        type='password'
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        error={formik.touched.password && Boolean(formik.errors.password)}
                                    />
                                </FormControl>
                                {formik.touched.password && formik.errors.password && (
                                    <FormHelperText error id="standard-weight-helper-text-password-board">
                                        {formik.errors.password}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={10}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="confirm_password"
                                        type={showPassword ? 'text' : 'password'}
                                        label="Confirm Password*"
                                        value={formik.values.confirm_password}
                                        onChange={formik.handleChange}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                                    />
                                </FormControl>
                                {formik.touched.confirm_password && formik.errors.confirm_password && (
                                    <FormHelperText error id="standard-weight-helper-text-confirm_password-board">
                                        {formik.errors.confirm_password}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                        color="error"
                                        onClick={closeDialog}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={formik.handleSubmit}
                                    >
                                        Change Password
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </MainCard>
            </DialogContent>
        </Dialog>
    );
};

export default ChangePassword;
